import React, { createContext, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'

import { SHOW_UPSELL } from '../../env'
import { DEFAULT_PLAN_SKU, TRIAL_SKU } from '../../constants'
import messages from '../../translations/messages'
import flattenMessages from '../../utils/intl'

// Who the site is (its order sources, links and tracking IDs) has no
// default: a key a site leaves out must fail, not quietly become another
// site's value. A key set to null counts as set, for a site that has no such
// ID yet.
export const REQUIRED_IDENTITY_KEYS = {
  sources: ['trial', 'checkout', 'backupTrial', 'abandonedCart', 'trialCta', 'newsletter'],
  links: ['terms', 'privacy', 'home'],
  tracking: ['gtmId', 'buyFlowGtmId', 'uetTagId', 'facebookPixelId', 'adsConversionSendTo', 'capterraTrackerSrc', 'successEvents', 'dataLayerPurchase'],
}

// Checkout content defaults to the existing screens. An app's config replaces
// whole top-level keys, never part of one, so a site that sets `tracking`
// cannot inherit another site's IDs by leaving one out.
export const DEFAULT_SITE_CONFIG = {
  sources: {},
  defaultSku: DEFAULT_PLAN_SKU,
  trialSku: TRIAL_SKU,
  planSelect: true,
  upsell: SHOW_UPSELL,
  backupSkuMap: {
    neatyr: 'neatnocc',
    neatyrt: 'neatnocc',
    neatvipyr: 'neatnocc',
    neataiyr: 'neatainocc',
    neataivipyr: 'neatainocc',
    neathomeyr: 'neathomenocc',
    neathomevipyr: 'neathomenocc',
    neatdocsyr: 'neatnocc',
    neatdocsyrt: 'neatnocc',
  },
  links: {},
  signUpHeading: { skuVariants: {}, variants: {} },
  panels: { accountSetup: null, billingTrial: null, billingBuy: null },
  integrationsSlot: null,
  messages: {},
  tracking: {},
}

export const missingIdentityKeys = (config) => Object.entries(REQUIRED_IDENTITY_KEYS)
  .flatMap(([group, keys]) => keys
    .filter((key) => config[group]?.[key] === undefined)
    .map((key) => `${group}.${key}`))

// What a missing required key does, decided in this one place.
const onMissingIdentityKeys = (missing) => {
  throw new Error(`Site config is missing required keys: ${missing.join(', ')}`)
}

export const resolveSiteConfig = (config) => {
  const resolved = { ...DEFAULT_SITE_CONFIG, ...config }
  const missing = missingIdentityKeys(resolved)
  if (missing.length) onMissingIdentityKeys(missing)
  return resolved
}

// An app's IntlProvider takes the shared copy with the site's messages laid
// over it by id, so a site changes a string without editing the shared file.
export const mergeMessages = (siteMessages) => ({ ...flattenMessages(messages['en-US']), ...siteMessages })

const SiteConfigContext = createContext(null)

export function SiteConfigProvider({ config, children }) {
  const value = useMemo(() => resolveSiteConfig(config), [config])

  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>
}

SiteConfigProvider.propTypes = {
  children: PropTypes.node,
  config: PropTypes.shape({}),
}

// With no provider mounted there is no site, so every identity key is missing.
export const useSiteConfig = () => useContext(SiteConfigContext) ?? resolveSiteConfig()
